// Centralised SEO copy + schema for Integrated Wellness.
// Entity architecture (consistent across every page + schema object):
//   Parent brand ......... Integrated Wellness
//   Medical entity ....... Integrated Wellness Medical Center
//   Technology platform .. Integrated Clinical Intelligence
//   Core technology ...... Integrated Matrix Engine

const SITE_URL = process.env.REACT_APP_SITE_URL;
const OG_IMAGE = process.env.REACT_APP_OG_IMAGE;
export { SITE_URL, OG_IMAGE };

// ─── Brand-level constants ─────────────────────────────────────────────────
export const BRAND = {
  name: "Integrated Wellness", // primary/parent brand
  medicalOrg: "Integrated Wellness Medical Center", // medical entity
  platform: "Integrated Clinical Intelligence", // technology platform
  engine: "Integrated Matrix Engine", // core technology
  alternateName: ["Integrated Clinical Intelligence", "Integrated Wellness Medical Center", "IWMC"],
  url: SITE_URL,
  ogImage: OG_IMAGE,
  founder: {
    name: "John Campetella",
    jobTitle: "Founder and CEO",
    description:
      "Founder, technologist, UFC veteran, and architect of the Integrated Clinical Intelligence platform.",
  },
  medicalDirector: {
    name: "Dr. Michael Ellis, DO",
    plainName: "Michael Ellis",
    jobTitle: "Medical Director",
    specialty: ["Internal Medicine", "Wellness Medicine", "Aesthetic Medicine"],
  },
  contact: {
    phone: "+1-201-535-3582",
    email: "Info@IntegratedWellnessNY.com",
    address: {
      streetAddress: "301 High Pointe Lane",
      addressLocality: "Neptune",
      addressRegion: "NJ",
      postalCode: "07753",
      addressCountry: "US",
    },
  },
};

// ─── Per-route OG image paths (generated via `yarn og` into /public/og/) ─
export const OG_IMAGES = {
  home: `${SITE_URL}/og/home.png`,
  getStarted: `${SITE_URL}/og/get-started.png`,
  contact: `${SITE_URL}/og/contact.png`,
  technology: `${SITE_URL}/og/technology.png`,
  team: `${SITE_URL}/og/team.png`,
  sampleAnalysis: `${SITE_URL}/og/sample-analysis.png`,
  medicalCare: `${SITE_URL}/og/medical-care.png`,
  wellnessOptimization: `${SITE_URL}/og/wellness-optimization.png`,
  medicalDirector: `${SITE_URL}/og/medical-director.png`,
  founderStory: `${SITE_URL}/og/founder-story.png`,
  aiAgents: `${SITE_URL}/og/ai-agents.png`,
  wearableIntegrations: `${SITE_URL}/og/wearable-integrations.png`,
};
export const programOgImage = (slug) => `${SITE_URL}/og/programs/${slug}.png`;
export const intentOgImage = (slug) => `${SITE_URL}/og/${slug}.png`;
export const evidenceOgImage = (slug) => `${SITE_URL}/og/evidence/${slug}.png`;

// ─── Reusable schema builders ──────────────────────────────────────────────
export function organizationSchema() {
  return {
    "@context": "https://schema.org",
    "@type": ["MedicalOrganization", "MedicalBusiness"],
    name: BRAND.medicalOrg,
    alternateName: BRAND.alternateName,
    url: BRAND.url,
    logo: `${BRAND.url}/logo.png`,
    image: BRAND.ogImage,
    telephone: BRAND.contact.phone,
    email: BRAND.contact.email,
    parentOrganization: {
      "@type": "Organization",
      name: BRAND.name,
      url: BRAND.url,
    },
    brand: { "@type": "Brand", name: BRAND.platform },
    knowsAbout: [
      "Precision Medicine",
      "Digital Twin Health",
      "Biological Age Testing",
      "Longevity Medicine",
      "Metabolic Health",
      "Hormone Optimization",
      "Genetic Testing",
      "Microbiome Testing",
    ],
    address: {
      "@type": "PostalAddress",
      ...BRAND.contact.address,
    },
    areaServed: { "@type": "Country", name: "United States" },
    founder: {
      "@type": "Person",
      name: BRAND.founder.name,
      jobTitle: BRAND.founder.jobTitle,
      description: BRAND.founder.description,
    },
    employee: {
      "@type": "Physician",
      name: BRAND.medicalDirector.name,
      jobTitle: BRAND.medicalDirector.jobTitle,
      medicalSpecialty: BRAND.medicalDirector.specialty,
    },
    availableService: [
      "Functional Medicine",
      "Hormone Optimization",
      "Longevity Medicine",
      "AI-Powered Wellness Optimization",
      "Personalized Lifestyle and Supplement Protocols",
      "Performance Wellness Coaching",
      "Digital Twin Health Analysis",
      "Telemedicine",
    ],
    medicalSpecialty: ["Functional Medicine", "Preventive Medicine"],
  };
}

export function websiteSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: BRAND.name,
    alternateName: BRAND.platform,
    url: BRAND.url,
    publisher: { "@type": "Organization", name: BRAND.name, url: BRAND.url },
  };
}

export function breadcrumbSchema(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: item.name,
      item: item.url.startsWith("http") ? item.url : `${SITE_URL}${item.url}`,
    })),
  };
}

export function faqSchema(faqs) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
}

export function medicalWebPageSchema({
  name,
  about,
  lastReviewed = new Date().toISOString().split("T")[0],
}) {
  return {
    "@context": "https://schema.org",
    "@type": "MedicalWebPage",
    name,
    about: {
      "@type": "MedicalCondition",
      name: about,
    },
    audience: { "@type": "MedicalAudience", audienceType: "Patient" },
    lastReviewed,
    reviewedBy: {
      "@type": "Physician",
      name: BRAND.medicalDirector.name,
      jobTitle: `${BRAND.medicalDirector.jobTitle}, ${BRAND.medicalOrg}`,
    },
  };
}

export function wellnessWebPageSchema({ name }) {
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name,
    reviewedBy: {
      "@type": "Organization",
      name: `${BRAND.name} Clinical Team`,
    },
  };
}

export function physicianSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Physician",
    name: BRAND.medicalDirector.name,
    jobTitle: BRAND.medicalDirector.jobTitle,
    worksFor: {
      "@type": "MedicalBusiness",
      name: BRAND.medicalOrg,
    },
    medicalSpecialty: BRAND.medicalDirector.specialty,
    description: `Medical Director at ${BRAND.medicalOrg}, overseeing clinical protocols and licensed medical care delivered through the ${BRAND.platform} platform.`,
  };
}

export function personSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Person",
    name: BRAND.founder.name,
    jobTitle: BRAND.founder.jobTitle,
    description: BRAND.founder.description,
    worksFor: { "@type": "Organization", name: BRAND.name },
  };
}
